/**
 * 赛事技能需求维护面板（扩展①，organizer/admin 专用）。
 *
 * 两条路径：
 * 1. 手动维护：直接填技能词（逗号分隔），PUT 覆盖式保存；
 * 2. AI 抽取：粘贴赛事通知原文 → POST extract → 归一技能列表（via 标注、不落库）→
 *    确认后走同一次 PUT 保存——LLM 永远不直接写业务表。
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { skillName } from '@crossmatch/shared';

import { gapApi } from '../api/gap.js';
import { toUserMessage } from '../api/errors.js';
import { Button } from './Button.js';
import { Tag } from './Tag.js';
import { useEventRequirementsQuery } from '../hooks/queries.js';
import { qk } from '../lib/queryKeys.js';
import { toast } from '../store/toast.js';

export function EventRequirementsPanel({ eventCode }: { eventCode: string }) {
  const queryClient = useQueryClient();
  const requirementsQuery = useEventRequirementsQuery(eventCode);
  const [skillsInput, setSkillsInput] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [extractInfo, setExtractInfo] = useState<string | null>(null);

  const current = requirementsQuery.data?.requirements ?? [];
  const inputValue = skillsInput ?? current.map((row) => skillName(row.skill)).join('，');

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: qk.eventRequirements(eventCode) });
    // 需求变化会影响所有挂该赛事队伍的缺口视图
    void queryClient.invalidateQueries({ queryKey: ['team'] });
  };

  const save = useMutation({
    mutationFn: (skills: string) => gapApi.setRequirements(eventCode, skills),
    onSuccess: (data) => {
      setSkillsInput(null);
      setExtractInfo(null);
      invalidate();
      toast.success('赛事需求已更新', `共 ${data.requirements.length} 项技能需求`);
    },
    onError: (error) => {
      toast.error('保存赛事需求失败', toUserMessage(error));
    },
  });

  const extract = useMutation({
    mutationFn: (text: string) => gapApi.extractRequirements(eventCode, text),
    onSuccess: (data) => {
      if (data.skills.length === 0) {
        setExtractInfo(`抽取完成（${data.via === 'llm' ? '大模型' : '规则'}）：没有识别出词表内技能`);
        return;
      }
      setSkillsInput(data.skills.map((skill) => skillName(skill)).join('，'));
      const unmatched = data.unmatched.length > 0 ? `；未收录：${data.unmatched.join('、')}` : '';
      setExtractInfo(
        `抽取完成（${data.via === 'llm' ? '大模型' : '规则'}兜底）：识别 ${data.skills.length} 项${unmatched}。确认无误后点「保存需求」。`,
      );
    },
    onError: (error) => {
      toast.error('抽取失败', toUserMessage(error));
    },
  });

  return (
    <div className="stack">
      <div className="row-tight">
        <span className="card-sub">当前需求（{current.length} 项）</span>
        {current.length === 0 ? <span className="muted small">尚未维护</span> : null}
        {current.map((row) => (
          <Tag key={row.skill}>{skillName(row.skill)}</Tag>
        ))}
      </div>

      <div className="field">
        <label className="field-label" htmlFor={`req-skills-${eventCode}`}>
          技能需求（逗号分隔，覆盖式保存）
        </label>
        <input
          id={`req-skills-${eventCode}`}
          className="input"
          value={inputValue}
          placeholder="如：python，算法，财务分析"
          onChange={(event) => setSkillsInput(event.target.value)}
        />
      </div>
      <div className="row">
        <Button
          variant="primary"
          size="sm"
          loading={save.isPending}
          disabled={inputValue.trim().length === 0}
          onClick={() => save.mutate(inputValue)}
        >
          保存需求
        </Button>
      </div>

      <div className="field">
        <label className="field-label" htmlFor={`req-notice-${eventCode}`}>
          从通知原文 AI 抽取（可选）
        </label>
        <textarea
          id={`req-notice-${eventCode}`}
          className="input"
          rows={4}
          value={notice}
          placeholder="粘贴挑战杯/国创的赛事通知原文，AI 抽取技能需求（只读建议，不落库）"
          onChange={(event) => setNotice(event.target.value)}
        />
        <span className="field-hint">抽取结果回填到上方输入框，确认后才写入。</span>
      </div>
      <div className="row">
        <Button
          variant="secondary"
          size="sm"
          loading={extract.isPending}
          disabled={notice.trim().length === 0}
          onClick={() => extract.mutate(notice)}
        >
          抽取技能需求
        </Button>
        {extractInfo !== null ? <span className="small muted wrap-anywhere">{extractInfo}</span> : null}
      </div>
    </div>
  );
}
